/**
 * Utility function to help keep track of items that are already fetched in a list.
 * Intended for redux actions that calls API to receive list of items, with the range between
 * `offset` & `limit`. This function will compare the `prevRange` to return a new range after
 * including fetched `offset` & `limit`.
 * @param {Array<[number, number]>} prevRange Previously fetched range as a base for comparison. Should be an empty string if no items were fetched before.
 * @param offset Starting index of items to be fetched in a list.
 * @param limit Total number of items to be fetched in a list
 * @returns {Array<[number, number]>} New range after including the range from `offset` & `limit`
 */
export function getNewFetchedRange(
  prevRange: Array<[number, number]>,
  offset: number,
  limit: number
): Array<[number, number]> {
  // Step 1: Update each range with the new offset & limit if there is an overlap
  const offsetEnd = offset + limit;
  let hasOverlap = false;
  const patchedRange: Array<[number, number]> = prevRange.map(range => {
    const start = range[0];
    const end = range[1];
    if (offset < start && offsetEnd > end) {
      hasOverlap = true;
      return [offset, offsetEnd];
    } else if (offset >= start && offset < end && offsetEnd > end) {
      hasOverlap = true;
      return [start, offsetEnd];
    } else if (offset < start && offsetEnd > start && offsetEnd <= end) {
      hasOverlap = true;
      return [offset, end];
    } else if (
      offset >= start &&
      offset < end &&
      offsetEnd > start &&
      offsetEnd <= end
    ) {
      hasOverlap = true;
    }
    return range;
  });

  // Step 2: If there's no overlap, then add a new range instead
  if (!hasOverlap) {
    patchedRange.push([offset, offsetEnd]);
  }

  // Step 3: Merge overlaps between ranges if any
  const finalRange = patchedRange
    .sort((a, b) => a[0] - b[0])
    .reduce((result: [number, number][], current: [number, number]) => {
      if (!result.length) {
        return [current];
      }
      const lastRange = result[result.length - 1];
      if (lastRange[1] >= current[0]) {
        return [
          ...result.slice(0, result.length - 1),
          [lastRange[0], current[1]],
        ] as Array<[number, number]>;
      }
      return [...result, current];
    }, [] as Array<[number, number]>);

  return finalRange;
}

/**
 * Utility function to help keep track of items that are already fetched in a list.
 * This function returns true if the range between `offset` & `limit` are already fully fetched in
 * `fetchedRange`. If the range is partially fetched (e.g, some range are fetch, some are not yet
 * fetched) or none has been fetched at all, it will return false.
 * @param {Array<[number, number]>} fetchedRange Current ranges that has already been fetched
 * @param offset Starting index of items to be fetched in a list.
 * @param limit Total number of items to be fetched in a list
 * @returns {boolean}
 */
export function isFetchedInRange(
  fetchedRange: Array<[number, number]>,
  offset: number,
  limit: number
) {
  let isFetched = false;
  const offsetEnd = offset + limit;
  fetchedRange.forEach(range => {
    const start = range[0];
    const end = range[1];
    if (
      offset >= start &&
      offset < end &&
      offsetEnd > start &&
      offsetEnd <= end
    ) {
      isFetched = true;
    }
  });

  return isFetched;
}
